import { normalizarPagamentoExterior } from './pagamentoExteriorPolicy';
import { identificarLinhaOrcamento } from './linhaOrcamentoIdentificacaoPolicy';
import { FeeIntelligence } from './feeIntelligence';
import { AgenciaOrcamentoPolicy } from './agenciaOrcamentoPolicy';

// Moeda do FORNECEDOR (decisão do Rafael, 27/09/2026; ampliada em 02/10; por ETAPA e por LINHA em 08/10).
// Espelho de accessapi app/Services/OrcamentoMoedaLinha.php — mesma regra, mesmos números:
// - a linha só tem moeda própria quando recebeu câmbio próprio (cambioPreEvento/cambioNegociado/
//   cambioFechamento); sem câmbio próprio é LEGADA: valores na moeda do orçamento × câmbio global (nada muda);
// - 02/10: qualquer linha de fornecedor (Brasil, Exterior ou Não informado) escolhe a moeda,
//   inclusive a da Access (contrato R$ exato); ficam de fora FEE e imposto/IOF/NF (seguem o orçamento);
// - 08/10 (Inter): cada etapa tem a SUA moeda e o SEU câmbio: `moeda` = Pré-evento; `moedaNegociado`/
//   `moedaFechamento` null = herda a etapa anterior (Negociado ← Pré, Fechamento ← Negociado);
//   `cambioFechamento` null = câmbio do Negociado. Valor digitado numa etapa está na moeda DA ETAPA;
//   R$ da etapa = valor × câmbio da etapa. Etapa em Real = câmbio 1; em orçamento em Real, a linha com
//   todas as etapas em Real = moeda do orçamento = legado (câmbios nulos);
// - Fechamento concluído (só Exterior) usa o câmbio real de cada transação;
// - 08/10: a escolha é por LINHA (não propaga mais para as linhas do mesmo fornecedor; a API não
//   exige mais unicidade por fornecedor × Local do pagamento).

export const MOEDAS_FORNECEDOR = [
    ['real', 'Real (BRL)'], ['dolar', 'Dólar (USD)'], ['euro', 'Euro (EUR)'], ['iene', 'Iene (JPY)'], ['libra', 'Libra (GBP)'], ['dolarcad', 'Dólar Canadense (CAD)'],
];
const SIMBOLOS = { real: 'R$', dolar: 'US$', euro: '€', iene: '¥', libra: '£', dolarcad: 'C$' };
export const CAMPO_FATOR_MOEDA = 'fatorMoedaOrcamento';

const numero = (valor) => {
    if (valor === null || valor === undefined) return null;
    if (typeof valor === 'number') return Number.isFinite(valor) ? valor : null;
    const texto = String(valor).trim();
    if (!texto) return null;
    const convertido = Number(texto.includes(',') ? texto.replace(/\./g, '').replace(',', '.') : texto);
    return Number.isFinite(convertido) ? convertido : null;
};
const cambioInformado = (valor) => {
    const n = numero(valor);
    return n !== null && n > 0 ? n : null;
};

export const normalizarMoeda = (moeda) => {
    const m = String(moeda || 'real').trim().toLowerCase();
    return Object.prototype.hasOwnProperty.call(SIMBOLOS, m) ? m : 'real';
};
export const simboloMoeda = (moeda) => SIMBOLOS[normalizarMoeda(moeda)];

export const MOMENTOS_MOEDA = ['PreEvento', 'Negociado', 'Fechamento'];
// Coluna da moeda e do câmbio de cada etapa (`moeda` = Pré-evento; as demais null = herda a anterior).
export const CAMPOS_MOEDA_ETAPA = { PreEvento: 'moeda', Negociado: 'moedaNegociado', Fechamento: 'moedaFechamento' };
export const CAMPOS_CAMBIO_ETAPA = { PreEvento: 'cambioPreEvento', Negociado: 'cambioNegociado', Fechamento: 'cambioFechamento' };
export const ROTULOS_ETAPA_MOEDA = { PreEvento: 'Pré-evento', Negociado: 'Negociado', Fechamento: 'Fechamento' };
// Ordem de busca do câmbio da etapa (só entre etapas na MESMA moeda): a própria, depois as vizinhas.
const ORDEM_CAMBIO = {
    PreEvento: ['PreEvento', 'Negociado', 'Fechamento'],
    Negociado: ['Negociado', 'PreEvento', 'Fechamento'],
    Fechamento: ['Fechamento', 'Negociado', 'PreEvento'],
};
const CAMPOS_NOVOS = ['moedaNegociado', 'moedaFechamento', 'cambioFechamento'];

export const temCambioProprio = (linha = {}) => cambioInformado(linha?.cambioPreEvento) !== null
    || cambioInformado(linha?.cambioNegociado) !== null
    || cambioInformado(linha?.cambioFechamento) !== null;

// 'pre'|'pre_evento'|'PreEvento' → PreEvento; 'neg'|'negociado'|'Negociado' → Negociado; demais → Fechamento.
export const momentoDaEtapa = (etapa = '') => {
    const e = String(etapa).toLowerCase();
    if (['pre', 'pre_evento', 'preevento'].includes(e)) return 'PreEvento';
    if (['neg', 'negociado'].includes(e)) return 'Negociado';
    return 'Fechamento';
};

// Moeda informada numa coluna de etapa (null/''/desconhecida = herda).
const moedaInformada = (moeda) => {
    if (moeda === null || moeda === undefined) return null;
    const m = String(moeda).trim().toLowerCase();
    return Object.prototype.hasOwnProperty.call(SIMBOLOS, m) ? m : null;
};

// Moeda PRÓPRIA da linha na etapa, com a herança (não olha se a linha é legada).
export function moedaPropriaEtapa(linha = {}, etapa = 'PreEvento') {
    const pre = normalizarMoeda(linha?.moeda);
    const momento = momentoDaEtapa(etapa);
    if (momento === 'PreEvento') return pre;
    const neg = moedaInformada(linha?.moedaNegociado) ?? pre;
    if (momento === 'Negociado') return neg;
    return moedaInformada(linha?.moedaFechamento) ?? neg;
}

// Moeda em que os valores da linha estão digitados NA ETAPA (padrão: Pré-evento). Legado = moeda do orçamento.
export const moedaDaLinhaFornecedor = (linha = {}, moedaOrcamento = 'real', etapa = 'PreEvento') => (temCambioProprio(linha)
    ? moedaPropriaEtapa(linha, etapa) : normalizarMoeda(moedaOrcamento));

// { PreEvento, Negociado, Fechamento } → moeda da linha em cada etapa.
export const moedasPorEtapaLinha = (linha = {}, moedaOrcamento = 'real') => MOMENTOS_MOEDA
    .reduce((acc, momento) => ({ ...acc, [momento]: moedaDaLinhaFornecedor(linha, moedaOrcamento, momento) }), {});

// R$ por 1 unidade da moeda da linha NA ETAPA. Legado = câmbio global informado. Etapa em Real = 1.
// Senão, o câmbio da etapa; vazio, o de outra etapa na MESMA moeda (Fechamento: Fech → Neg → Pré) —
// dado antigo: Fechamento = Negociado, senão Pré (como antes). Sem câmbio na moeda: câmbio global.
export function cambioBrlLinha(linha = {}, etapa = 'PreEvento', cambioGlobal = 1) {
    if (!temCambioProprio(linha)) return cambioGlobal;
    const momento = momentoDaEtapa(etapa);
    const moeda = moedaPropriaEtapa(linha, momento);
    if (moeda === 'real') return 1;
    for (const outro of ORDEM_CAMBIO[momento]) {
        if (moedaPropriaEtapa(linha, outro) !== moeda) continue;
        const c = cambioInformado(linha[CAMPOS_CAMBIO_ETAPA[outro]]);
        if (c !== null) return c;
    }
    return cambioGlobal;
}

// Fator linha → moeda do orçamento. Legado = 1 (nenhuma conta muda).
export function fatorMoedaOrcamento(linha = {}, etapa = 'PreEvento', cambioGlobal = 1) {
    if (!temCambioProprio(linha)) return 1;
    const global = Number.isFinite(cambioGlobal) && cambioGlobal > 0 ? cambioGlobal : 1;
    return cambioBrlLinha(linha, etapa, global) / global;
}

export const cambioGlobalOrcamento = (orcamento = {}) => {
    if (normalizarMoeda(orcamento?.moeda) === 'real') return 1;
    const c = numero(orcamento?.cambio);
    return c !== null && c > 0 ? c : 1;
};

// Percorre grupo → subitem devolvendo novas referências só onde a linha mudou.
// fn(linha, grupoIndex, linhaIndex) → linha (mesma referência = sem mudança).
function mapearLinhas(orcamentoitem = [], fn) {
    if (!Array.isArray(orcamentoitem)) return orcamentoitem;
    return orcamentoitem.map((grupo, g) => {
        if (!grupo || !Array.isArray(grupo.subitem)) return grupo;
        let mudou = false;
        const subitem = grupo.subitem.map((linha, l) => {
            if (!linha) return linha;
            const nova = fn(linha, g, l);
            if (nova !== linha) mudou = true;
            return nova;
        });
        return mudou ? { ...grupo, subitem } : grupo;
    });
}

// Anota em cada linha de moeda própria o fator por momento do FEE (feeIntelligence.rawBaseByMoment);
// remove das demais. Mesmo contrato de OrcamentoMoedaLinha::anotarFatores. Devolve NOVOS grupos/linhas
// (não muta o estado); linha sem fator e sem anotação antiga mantém a mesma referência.
export function anotarFatoresMoeda(orcamentoitem = [], cambioGlobal = 1) {
    return mapearLinhas(orcamentoitem, (linha) => {
        const proprio = temCambioProprio(linha);
        if (!proprio && !Object.prototype.hasOwnProperty.call(linha, CAMPO_FATOR_MOEDA)) return linha;
        const semFator = { ...linha };
        delete semFator[CAMPO_FATOR_MOEDA];
        if (!proprio) return semFator;
        return {
            ...semFator,
            [CAMPO_FATOR_MOEDA]: {
                pre_evento: fatorMoedaOrcamento(linha, 'PreEvento', cambioGlobal),
                negociado: fatorMoedaOrcamento(linha, 'Negociado', cambioGlobal),
                fechamento: fatorMoedaOrcamento(linha, 'Fechamento', cambioGlobal),
            },
        };
    });
}

// Fator anotado (ou 1). `momento` no formato do feeIntelligence (pre_evento/negociado/fechamento).
export const fatorAnotado = (linha = {}, momento = 'pre_evento') => {
    const f = linha?.[CAMPO_FATOR_MOEDA]?.[momento];
    return typeof f === 'number' && Number.isFinite(f) ? f : 1;
};

export const idFornecedorLinha = (linha = {}) => {
    const pagador = linha?.fornecedorpagamento?.id ?? linha?.fornecedorpagamento;
    if (pagador && !['agencia', 'cliente'].includes(String(pagador))) return String(pagador);
    const f = linha?.fornecedor?.id ?? linha?.fornecedor;
    return f === null || f === undefined ? '' : String(f);
};

// Local do pagamento da linha: 'exterior' | 'brasil' | 'nao_informado' (valor inválido = não informado).
export const LOCAL_EXTERIOR = 'exterior';
export const LOCAL_BRASIL = 'brasil';
export const LOCAL_NAO_INFORMADO = 'nao_informado';
export const localPagamentoLinha = (linha = {}) => {
    let valor = null;
    try { valor = normalizarPagamentoExterior(linha?.pagamento_exterior); } catch (e) { valor = null; }
    if (valor === true) return LOCAL_EXTERIOR;
    if (valor === false) return LOCAL_BRASIL;
    return LOCAL_NAO_INFORMADO;
};
export const rotuloLocalPagamento = (local) => ({ [LOCAL_EXTERIOR]: 'Exterior', [LOCAL_BRASIL]: 'Brasil' }[local] || 'Não informado');
// Texto de ajuda do seletor (08/10): a moeda e o câmbio valem só para a linha, por etapa da negociação.
export const textoAlcanceMoedaFornecedor = () => 'Vale só para esta linha; cada etapa tem a sua moeda e o seu câmbio.';

// Imposto 1945/NF Access, linhas calculadas de remessa e linhas de FEE seguem a moeda do orçamento
// (sem seletor). Mesma lista de OrcamentoMoedaLinha::linhaSemMoedaPropria (API).
export const linhaSemMoedaPropria = (linha = {}) => {
    const codigo = String(linha?.produto?.codigo ?? '').trim().toUpperCase();
    const produto = linha?.produto ?? {};
    return ['1945', 'IMPOSTO_NF_ACCESS', '130'].includes(codigo)
        || !!linha.linhaCalculadaImpostoAccess || !!linha.linhaVirtualImpostoNfAccess
        || !!linha.impostoNfAccessManualAgenciaChiesi || !!linha.impostoNfAccessFeeCanonicoChiesi
        || !!linha.linhaCalculadaRemessa || !!linha.linhaCalculadaRemessaEstimativa
        || FeeIntelligence.isFeeCalculatorItem(produto) || AgenciaOrcamentoPolicy.isFeeAgenciaGlobal(produto);
};

// R1 (02/10): qualquer linha de fornecedor escolhe a moeda, em qualquer Local do pagamento.
// Contrato "R$ exato" (02/10, regra 1): a linha da ACCESS também escolhe (RSVP, Inclusão, Fee aéreo,
// Outras taxas do grupo 130, plantões, coordenação). Continuam fora só a própria linha de FEE
// percentual, o imposto 1945/NF e as linhas automáticas de IOF/remessa/NF (linhaSemMoedaPropria).
// `fornecedorAccess` fica na assinatura (chamadas existentes); não exclui mais a Access.
export const linhaAceitaMoedaFornecedor = (linha = {}, fornecedorAccess = null) => !!linha && !linhaSemMoedaPropria(linha);

// Chave da regra de unicidade (R6): fornecedor × Local do pagamento. Linha sem fornecedor = só ela.
export const chaveFornecedorLocal = (linha = {}) => {
    const fornecedor = idFornecedorLinha(linha);
    return fornecedor ? `${fornecedor}|${localPagamentoLinha(linha)}` : '';
};

const ESTADO_LEGADO = { moeda: 'real', cambioPreEvento: null, cambioNegociado: null, moedaNegociado: null, moedaFechamento: null, cambioFechamento: null };
const ESTADO_REAL = { moeda: 'real', cambioPreEvento: 1, cambioNegociado: 1, moedaNegociado: null, moedaFechamento: null, cambioFechamento: null };
const estadoMoedaLinha = (linha = {}) => (temCambioProprio(linha)
    ? {
        moeda: normalizarMoeda(linha.moeda), cambioPreEvento: linha.cambioPreEvento ?? null, cambioNegociado: linha.cambioNegociado ?? null,
        moedaNegociado: moedaInformada(linha.moedaNegociado), moedaFechamento: moedaInformada(linha.moedaFechamento), cambioFechamento: linha.cambioFechamento ?? null,
    }
    : ESTADO_LEGADO);
const mesmoEstado = (a, b) => Object.keys(ESTADO_LEGADO).every((campo) => (a[campo] ?? null) === (b[campo] ?? null));
// Nova linha só quando algo muda (legado → legado mantém a referência e a marca de moeda gravada).
// Os campos de 08/10 (moedaNegociado/moedaFechamento/cambioFechamento) só entram na linha quando têm
// valor ou já existiam nela (linha antiga continua com as mesmas chaves).
const comEstadoMoeda = (linha, estado) => {
    if (!temCambioProprio(estado) && !temCambioProprio(linha)) return linha;
    if (mesmoEstado(estadoMoedaLinha(linha), estado)) return linha;
    const nova = { ...linha };
    Object.keys(ESTADO_LEGADO).forEach((campo) => {
        const valor = estado[campo] ?? null;
        if (CAMPOS_NOVOS.includes(campo) && valor === null && !Object.prototype.hasOwnProperty.call(linha, campo)) return;
        nova[campo] = valor;
    });
    return nova;
};
const moedaDoOrcamento = (orcamento = {}, opcoes = {}) => normalizarMoeda(opcoes.moedaOrcamento ?? orcamento.moeda);
const linhaEm = (orcamento, grupoIndex, linhaIndex) => orcamento?.orcamentoitem?.[grupoIndex]?.subitem?.[linhaIndex] ?? null;

// Arredondamento a N casas (meio para longe do zero, com pré-arredondamento a 15 dígitos, como o PHP).
const arredondarCasas = (valor, casas) => {
    if (!valor) return 0;
    const fator = 10 ** casas;
    return Math.sign(valor) * Math.round(Number((Math.abs(valor) * fator).toPrecision(15))) / fator;
};
// Unitários que dependem da moeda da linha (etapas + colunas adicionais de Negociado).
const CAMPOS_UNITARIOS = ['valorUnitarioPreEvento', 'valorUnitarioNegociado', 'valorUnitarioFechamento'];
const PREFIXO_ADICIONAL = 'valorUnitarioNegociadoAdicional_';
const etapaDoCampo = (campo) => {
    if (campo.endsWith('PreEvento')) return 'PreEvento';
    if (campo.endsWith('Fechamento')) return 'Fechamento';
    return 'Negociado';
};

// Item 1 (Rafael 02/10, pós-QA): a etapa que TROCA de moeda tem os unitários convertidos para manter o
// valor em reais: novo = antigo × câmbio antigo da etapa ÷ câmbio novo da etapa (Real = 1; moeda própria
// = câmbio dela na etapa; moeda do orçamento = câmbio do orçamento). Real fica com 2 casas; moeda
// estrangeira com 6 (precisão da coluna), para a volta dar o mesmo R$. 08/10: por ETAPA — só as colunas
// da etapa cuja moeda mudou. Mesma moeda (só câmbio mudou) ou câmbio ausente na etapa: não converte.
// Devolve a mesma referência quando nada muda.
export function converterValoresMoedaLinha(antiga = {}, nova = {}, { cambioGlobal = 1, moedaOrcamento = 'real' } = {}) {
    const global = Number.isFinite(cambioGlobal) && cambioGlobal > 0 ? cambioGlobal : 1;
    const campos = [...CAMPOS_UNITARIOS, ...Object.keys(antiga).filter((campo) => campo.startsWith(PREFIXO_ADICIONAL))];
    let convertida = nova;
    campos.forEach((campo) => {
        const etapa = etapaDoCampo(campo);
        const para = moedaDaLinhaFornecedor(nova, moedaOrcamento, etapa);
        if (moedaDaLinhaFornecedor(antiga, moedaOrcamento, etapa) === para) return;
        const valor = numero(antiga[campo]);
        if (valor === null) return;
        const cambioDe = cambioInformado(cambioBrlLinha(antiga, etapa, global));
        const cambioPara = cambioInformado(cambioBrlLinha(nova, etapa, global));
        if (cambioDe === null || cambioPara === null) return;
        if (convertida === nova) convertida = { ...nova };
        convertida[campo] = arredondarCasas(valor * cambioDe / cambioPara, para === 'real' ? 2 : 6);
    });
    return convertida;
}
const comEstadoMoedaConvertido = (linha, estado, contextoConversao) => {
    const nova = comEstadoMoeda(linha, estado);
    return nova === linha ? linha : converterValoresMoedaLinha(linha, nova, contextoConversao);
};
const contextoConversao = (orcamento = {}, opcoes = {}) => {
    const moedaOrcamento = moedaDoOrcamento(orcamento, opcoes);
    return { moedaOrcamento, cambioGlobal: cambioGlobalOrcamento({ ...orcamento, moeda: moedaOrcamento }) };
};

// Aplica o estado da moeda SÓ à linha (grupoIndex, linhaIndex) — 08/10: a escolha é por linha —,
// convertendo os unitários das etapas que trocam de moeda (item 1). Devolve um novo orçamento (não muta).
function aplicarEstadoNaLinha(orcamento = {}, grupoIndex, linhaIndex, estado, opcoes = {}) {
    const referencia = linhaEm(orcamento, grupoIndex, linhaIndex);
    if (!referencia || !linhaAceitaMoedaFornecedor(referencia, opcoes.fornecedorAccess ?? null)) return orcamento;
    const nova = comEstadoMoedaConvertido(referencia, estado, contextoConversao(orcamento, opcoes));
    if (nova === referencia) return orcamento;
    return {
        ...orcamento,
        orcamentoitem: mapearLinhas(orcamento.orcamentoitem, (linha, g, l) => (g === grupoIndex && l === linhaIndex ? nova : linha)),
    };
}

// Estado (moeda + câmbios das 3 etapas) com a moeda da etapa trocada. `moeda` null em Negociado/Fechamento
// = herda a etapa anterior. Etapa cuja moeda efetiva mudou perde o câmbio (Real = 1; estrangeira =
// `cambios.sugerido` ou vazio); `cambios` = { cambioPreEvento, cambioNegociado, cambioFechamento,
// sugerido } — os câmbios informados valem por cima ('' = vazio). Linha legada parte da moeda do
// orçamento em todas as etapas (câmbio = o global). Orçamento em Real com tudo em Real = legado.
export function estadoMoedaEtapa(linha = {}, etapa = 'PreEvento', moeda = null, cambios = {}, opcoes = {}) {
    const moedaOrcamento = normalizarMoeda(opcoes.moedaOrcamento ?? 'real');
    const cambioGlobal = Number.isFinite(opcoes.cambioGlobal) && opcoes.cambioGlobal > 0 ? opcoes.cambioGlobal : 1;
    const momento = momentoDaEtapa(etapa);
    const antes = moedasPorEtapaLinha(linha, moedaOrcamento);
    const estado = temCambioProprio(linha) ? { ...estadoMoedaLinha(linha) } : {
        ...ESTADO_LEGADO, moeda: moedaOrcamento,
        cambioPreEvento: moedaOrcamento === 'real' ? 1 : cambioGlobal, cambioNegociado: moedaOrcamento === 'real' ? 1 : cambioGlobal,
    };
    const nova = moedaInformada(moeda);
    if (momento === 'PreEvento') estado.moeda = nova ?? moedaOrcamento;
    else estado[CAMPOS_MOEDA_ETAPA[momento]] = nova;
    // Herança canônica: moeda igual à da etapa anterior = null (segue a anterior).
    if (estado.moedaNegociado === estado.moeda) estado.moedaNegociado = null;
    if (estado.moedaFechamento === (estado.moedaNegociado ?? estado.moeda)) estado.moedaFechamento = null;
    MOMENTOS_MOEDA.forEach((m) => {
        const depois = moedaPropriaEtapa(estado, m);
        if (depois === antes[m]) return;
        const campo = CAMPOS_CAMBIO_ETAPA[m];
        if (depois === 'real') estado[campo] = m === 'Fechamento' ? null : 1;
        else if (m === 'Fechamento' && estado.moedaFechamento === null) estado[campo] = null; // herda o do Negociado
        else estado[campo] = cambios.sugerido ?? null;
    });
    ['cambioPreEvento', 'cambioNegociado', 'cambioFechamento'].forEach((campo) => {
        if (Object.prototype.hasOwnProperty.call(cambios, campo)) {
            estado[campo] = cambios[campo] === '' || cambios[campo] === undefined ? null : cambios[campo];
        }
    });
    const todasReal = MOMENTOS_MOEDA.every((m) => moedaPropriaEtapa(estado, m) === 'real');
    if (todasReal) return moedaOrcamento === 'real' ? ESTADO_LEGADO : { ...ESTADO_REAL };
    return estado;
}

// Seletor de moeda da ETAPA (08/10): moeda + câmbio só desta linha. `moeda` null em Negociado/Fechamento =
// "Mesma do Pré-evento/Negociado". Devolve um novo orçamento.
export function aplicarMoedaEtapa(orcamento = {}, grupoIndex, linhaIndex, etapa, moeda, cambios = {}, opcoes = {}) {
    const referencia = linhaEm(orcamento, grupoIndex, linhaIndex);
    if (!referencia) return orcamento;
    const conversao = contextoConversao(orcamento, opcoes);
    const estado = estadoMoedaEtapa(referencia, etapa, moeda, cambios, conversao);
    return aplicarEstadoNaLinha(orcamento, grupoIndex, linhaIndex, estado, opcoes);
}

// Câmbio de uma etapa da linha (sem trocar moedas). '' = vazio.
export function aplicarCambioEtapa(orcamento = {}, grupoIndex, linhaIndex, etapa, valor, opcoes = {}) {
    const referencia = linhaEm(orcamento, grupoIndex, linhaIndex);
    if (!referencia || !temCambioProprio(referencia)) return orcamento;
    const campo = CAMPOS_CAMBIO_ETAPA[momentoDaEtapa(etapa)];
    const estado = { ...estadoMoedaLinha(referencia), [campo]: valor === '' || valor === undefined ? null : valor };
    return aplicarEstadoNaLinha(orcamento, grupoIndex, linhaIndex, estado, opcoes);
}

// Seletor da moeda do Pré-evento (contrato de antes, 27/09): a moeda vale para a linha toda quando as
// demais etapas herdam. 'real' grava 1/1; em orçamento em Real, "Real" = moeda do orçamento = legado (R3).
// `cambios` = { cambioPreEvento, cambioNegociado, cambioFechamento } (só os informados mudam; '' = vazio).
// 08/10: só a própria linha muda.
export function aplicarMoedaFornecedor(orcamento = {}, grupoIndex, linhaIndex, moeda, cambios = {}, opcoes = {}) {
    return aplicarMoedaEtapa(orcamento, grupoIndex, linhaIndex, 'PreEvento', normalizarMoeda(moeda), cambios, opcoes);
}

// "Moeda do orçamento" no seletor: a linha volta ao legado (08/10: só ela).
export function removerMoedaFornecedor(orcamento = {}, grupoIndex, linhaIndex, opcoes = {}) {
    return aplicarEstadoNaLinha(orcamento, grupoIndex, linhaIndex, ESTADO_LEGADO, opcoes);
}

// R2 (02/10): a linha que MUDOU de Local do pagamento OU de fornecedor recebe o padrão do novo local
// (08/10: continua só como PADRÃO — depois cada linha escolhe a sua moeda por etapa):
// 1) se o fornecedor já tem outras linhas nesse local, segue a moeda/câmbios delas (linhas legadas
//    continuam legadas — nenhum número antigo muda);
// 2) senão, Brasil em orçamento que NÃO é em Real começa em Real (câmbios 1/1);
// 3) senão (Exterior, Não informado, orçamento em Real), moeda do orçamento (legado).
// Só a linha alterada muda. Devolve um novo orçamento.
export function aplicarPadraoMoedaLocal(orcamento = {}, grupoIndex, linhaIndex, opcoes = {}) {
    const linha = linhaEm(orcamento, grupoIndex, linhaIndex);
    if (!linha) return orcamento;
    const fornecedorAccess = opcoes.fornecedorAccess ?? null;
    let estado = ESTADO_LEGADO;
    if (linhaAceitaMoedaFornecedor(linha, fornecedorAccess)) {
        const chave = chaveFornecedorLocal(linha);
        const irmas = [];
        if (chave) {
            (orcamento.orcamentoitem || []).forEach((grupo, g) => (grupo?.subitem || []).forEach((outra, l) => {
                if (!outra || (g === grupoIndex && l === linhaIndex)) return;
                if (chaveFornecedorLocal(outra) === chave && linhaAceitaMoedaFornecedor(outra, fornecedorAccess)) irmas.push(outra);
            }));
        }
        if (irmas.length > 0) {
            const comMoeda = irmas.find((outra) => temCambioProprio(outra));
            estado = comMoeda ? estadoMoedaLinha(comMoeda) : ESTADO_LEGADO;
        } else if (chave && localPagamentoLinha(linha) === LOCAL_BRASIL && moedaDoOrcamento(orcamento, opcoes) !== 'real') {
            // Só com fornecedor escolhido (QA 02/10): sem fornecedor não dá para saber o grupo; o padrão
            // é aplicado de novo quando o fornecedor for escolhido (a linha segue o grupo dele).
            estado = ESTADO_REAL;
        }
    }
    // Item 1: a linha que muda de moeda pelo padrão do local também mantém o valor em reais.
    const nova = comEstadoMoedaConvertido(linha, estado, contextoConversao(orcamento, opcoes));
    if (nova === linha) return orcamento;
    return {
        ...orcamento,
        orcamentoitem: mapearLinhas(orcamento.orcamentoitem, (atual, g, l) => (g === grupoIndex && l === linhaIndex ? nova : atual)),
    };
}

// Linha sem seletor (FEE, imposto/NF) com câmbio próprio volta ao legado, como a API faz.
// (Até 01/10 isso valia para toda linha fora do Exterior; desde 02/10 o Local não limpa a moeda.)
export function limparMoedaLinhasSemSeletor(orcamentoitem = [], fornecedorAccess = null) {
    return mapearLinhas(orcamentoitem, (linha) => (linhaAceitaMoedaFornecedor(linha, fornecedorAccess) || !temCambioProprio(linha)
        ? linha : comEstadoMoeda(linha, ESTADO_LEGADO)));
}

// Passo único do recálculo da tela (antes de anotarFatoresMoeda):
// 1) linha sem seletor perde o câmbio próprio; 2) orçamento em Real: linha com TODAS as etapas em Real =
// legado (R3). Não muta; linhas sem mudança mantêm a referência. (Até 07/10 havia o passo 3, herança
// por fornecedor × Local; desde 08/10 a moeda é por linha e uma linha não copia a da outra.)
export function normalizarMoedaFornecedorItens(orcamentoitem = [], opcoes = {}) {
    const fornecedorAccess = opcoes.fornecedorAccess ?? null;
    let itens = limparMoedaLinhasSemSeletor(orcamentoitem, fornecedorAccess);
    if (normalizarMoeda(opcoes.moedaOrcamento) === 'real') {
        itens = mapearLinhas(itens, (linha) => (temCambioProprio(linha) && MOMENTOS_MOEDA.every((m) => moedaPropriaEtapa(linha, m) === 'real')
            ? comEstadoMoeda(linha, ESTADO_LEGADO) : linha));
    }
    return itens;
}

// Opções do seletor do Pré-evento. Orçamento em Real: "Real" é a própria moeda do orçamento (sem opção duplicada).
export const opcoesMoedaFornecedor = (moedaOrcamento = 'real') => (normalizarMoeda(moedaOrcamento) === 'real'
    ? MOEDAS_FORNECEDOR.filter(([valor]) => valor !== 'real') : MOEDAS_FORNECEDOR);

// Pendências (mesma regra do 422 da API): câmbio faltando numa etapa em moeda estrangeira (Pré e
// Negociado sempre; Fechamento só com moeda própria diferente da do Negociado). Cada pendência aponta
// para o câmbio vazio da etapa (alvo.campo = 'cambioPreEvento' | 'cambioNegociado' | 'cambioFechamento').
// Desde 08/10 não há mais a pendência "mesmo fornecedor com moeda diferente" (a moeda é por linha).
export function listarPendenciasMoedaFornecedor(orcamento = {}, fornecedorAccess = null) {
    const pendencias = [];
    (orcamento.orcamentoitem || []).forEach((grupo, grupoIndex) => (grupo?.subitem || []).forEach((linha, linhaIndex) => {
        if (!linha || !linhaAceitaMoedaFornecedor(linha, fornecedorAccess) || !temCambioProprio(linha)) return;
        const local = identificarLinhaOrcamento(grupo, linhaIndex, linha, 'Serviço');
        const exigidas = ['PreEvento', 'Negociado'];
        if (moedaInformada(linha.moedaFechamento) !== null) exigidas.push('Fechamento');
        const vazia = exigidas.find((m) => moedaPropriaEtapa(linha, m) !== 'real' && cambioInformado(linha[CAMPOS_CAMBIO_ETAPA[m]]) === null);
        if (!vazia) return;
        // "Mostrar campo" leva ao câmbio VAZIO da etapa (não ao seletor de moeda).
        pendencias.push({
            mensagem: `${local}: informe o câmbio de ${ROTULOS_ETAPA_MOEDA[vazia]} da moeda do fornecedor (${simboloMoeda(moedaPropriaEtapa(linha, vazia))}).`,
            grupoIndex, linhaIndex, etapa: null, campo: CAMPOS_CAMBIO_ETAPA[vazia],
        });
    }));
    return pendencias;
}

// Moedas próprias em uso no orçamento, por fornecedor × Local × combinação de moedas/câmbios (08/10: o
// mesmo fornecedor pode ter mais de uma): [{ fornecedorId, chave, nome, local, rotuloLocal, moeda, simbolo,
// cambioPreEvento, cambioNegociado, cambioFechamento, moedas: {PreEvento, Negociado, Fechamento},
// porEtapa: boolean (moedas diferentes entre as etapas) }]. `moeda`/`simbolo` = Pré-evento.
export function listarMoedasFornecedores(orcamento = {}, fornecedorAccess = null) {
    const porChave = new Map();
    (orcamento.orcamentoitem || []).forEach((grupo) => (grupo?.subitem || []).forEach((linha) => {
        if (!linha || !temCambioProprio(linha) || !linhaAceitaMoedaFornecedor(linha, fornecedorAccess)) return;
        const moedas = moedasPorEtapaLinha(linha);
        const cambios = MOMENTOS_MOEDA.map((m) => cambioBrlLinha(linha, m, 1));
        const base = chaveFornecedorLocal(linha) || `linha:${linha.id ?? linha.chaveunica ?? ''}`;
        const chave = `${base}|${MOMENTOS_MOEDA.map((m, i) => `${moedas[m]}:${cambios[i]}`).join('|')}`;
        if (porChave.has(chave)) return;
        const local = localPagamentoLinha(linha);
        porChave.set(chave, {
            fornecedorId: idFornecedorLinha(linha) || base, chave,
            nome: linha.fornecedor?.nome || linha.fornecedor?.nomefantasia || 'Fornecedor',
            local, rotuloLocal: rotuloLocalPagamento(local),
            moeda: moedas.PreEvento, simbolo: simboloMoeda(moedas.PreEvento),
            cambioPreEvento: cambios[0], cambioNegociado: cambios[1], cambioFechamento: cambios[2],
            moedas, porEtapa: new Set(Object.values(moedas)).size > 1,
        });
    }));
    return [...porChave.values()];
}

// Painel/listagem (27/09): valor na moeda do orçamento + câmbio global e as moedas próprias
// ESTRANGEIRAS dos fornecedores (qualquer Local; linhas planas ou agrupadas). Só exibição; o R$
// continua sendo valorRemessaExteriorPainel. 08/10: cada moeda estrangeira usada em alguma etapa da
// linha; câmbio = o do Negociado se ele estiver nessa moeda, senão Fechamento, senão Pré-evento.
export function detalheMoedasOrcamento(orcamento = {}) {
    const linhas = [];
    (orcamento.orcamentoitem || []).forEach((item) => {
        if (Array.isArray(item?.subitem)) item.subitem.forEach((l) => linhas.push(l));
        else if (item && item.superior) linhas.push(item);
    });
    const moedas = new Map();
    linhas.forEach((linha) => {
        if (!linha || !temCambioProprio(linha)) return;
        const porEtapa = moedasPorEtapaLinha(linha);
        new Set(Object.values(porEtapa)).forEach((moedaEtapa) => {
            if (moedaEtapa === 'real') return;
            const referencia = ['Negociado', 'Fechamento', 'PreEvento'].find((m) => porEtapa[m] === moedaEtapa);
            const cambioNegociado = cambioBrlLinha(linha, referencia, 1);
            const chave = `${moedaEtapa}|${cambioNegociado}`;
            if (!moedas.has(chave)) {
                moedas.set(chave, {
                    moeda: moedaEtapa, simbolo: simboloMoeda(moedaEtapa),
                    cambioPreEvento: porEtapa.PreEvento === moedaEtapa ? cambioBrlLinha(linha, 'PreEvento', 1) : cambioNegociado,
                    cambioNegociado,
                });
            }
        });
    });
    const moedaOrcamento = normalizarMoeda(orcamento.moeda);
    return { moedaOrcamento, simbolo: simboloMoeda(moedaOrcamento), cambio: cambioGlobalOrcamento(orcamento), fornecedores: [...moedas.values()] };
}
